// src/pages/Jobs.jsx
import React, { useState, useEffect } from 'react';
import PageTitle from '../components/SEO/PageTitle';
import { useAuth } from '../contexts/AuthContext';
import { getAllJobs, trackApplication } from '../services/jobs';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useLocation } from 'react-router-dom';
import JobCard from '../components/Jobs/JobCard';
import JobFilters from '../components/Jobs/JobFilters';
import JobDetailModal from '../components/Jobs/JobDetailModal';
import Pagination from '../components/Jobs/Pagination';
import JobSearchBar from '../components/Jobs/JobSearchBar';
import { searchJobs, getSimilarJobs } from '../utils/search';

// Profile completion check - using merged data from both collections
const getCompletionPercentage = (profile, userData) => {
    if (!profile) return 0;
    
    const firstName = profile.firstName || userData?.firstName || '';
    const lastName = profile.lastName || userData?.lastName || '';
    
    const fields = [
        { value: firstName, label: 'First Name' },
        { value: lastName, label: 'Last Name' },
        { value: profile.phoneNumber, label: 'Phone Number' },
        { value: profile.city, label: 'City' },
        { value: profile.country, label: 'Country' },
        { value: profile.gender, label: 'Gender' },
        { value: profile.dateOfBirth, label: 'Date of Birth' },
        { value: profile.lookingFor, label: 'What are you looking for?' },
        { value: profile.preferredJobLocation, label: 'Preferred Job Location' },
        { value: profile.desiredSalary, label: 'Desired Salary' },
        { value: profile.professionalSummary, label: 'Professional Summary' }
    ];
    
    let completed = 0;
    let total = fields.length;
    
    fields.forEach(field => {
        if (field.value?.trim()) completed++;
    });
    
    if (profile.skills?.length >= 3) completed++;
    total++;
    
    if (profile.workExperience?.length > 0 || profile.education?.length > 0) completed++;
    total++;
    
    if (profile.citizenship === 'South African') {
        total++;
        if (profile.idNumber?.trim() && /^\d{13}$/.test(profile.idNumber)) completed++;
    }
    
    return Math.round((completed / total) * 100);
};

const getMissingFields = (profile, userData) => {
    if (!profile) return [];
    const missing = [];
    
    const firstName = profile.firstName || userData?.firstName || '';
    const lastName = profile.lastName || userData?.lastName || '';
    
    if (!firstName?.trim()) missing.push('First Name');
    if (!lastName?.trim()) missing.push('Last Name');
    if (!profile.phoneNumber?.trim()) missing.push('Phone Number');
    if (!profile.city?.trim()) missing.push('City');
    if (!profile.country?.trim()) missing.push('Country');
    if (!profile.gender) missing.push('Gender');
    if (!profile.dateOfBirth) missing.push('Date of Birth');
    
    if (profile.citizenship === 'South African') {
        if (!profile.idNumber?.trim()) missing.push('ID Number (South African)');
        else if (!/^\d{13}$/.test(profile.idNumber)) missing.push('Valid ID Number (13 digits)');
    }
    
    if (!profile.lookingFor?.trim()) missing.push('What are you looking for?');
    if (!profile.preferredJobLocation?.trim()) missing.push('Preferred Job Location');
    if (!profile.desiredSalary?.trim()) missing.push('Desired Salary');
    if (!profile.professionalSummary?.trim()) missing.push('Professional Summary');
    if (profile.skills?.length < 3) missing.push(`Skills (need ${3 - (profile.skills?.length || 0)} more)`);
    if (profile.workExperience?.length === 0 && profile.education?.length === 0) {
        missing.push('Work Experience OR Education (at least one)');
    }
    
    return missing;
};

const isProfileComplete = (profile, userData) => {
    if (!profile) return false;
    
    const firstName = profile.firstName || userData?.firstName || '';
    const lastName = profile.lastName || userData?.lastName || '';
    
    if (!firstName?.trim()) return false;
    if (!lastName?.trim()) return false;
    if (!profile.phoneNumber?.trim()) return false;
    if (!profile.city?.trim()) return false;
    if (!profile.country?.trim()) return false;
    if (!profile.gender) return false;
    if (!profile.dateOfBirth) return false;
    
    if (profile.citizenship === 'South African') {
        if (!profile.idNumber?.trim()) return false;
        if (!/^\d{13}$/.test(profile.idNumber)) return false;
    }
    
    if (!profile.lookingFor?.trim()) return false;
    if (!profile.preferredJobLocation?.trim()) return false;
    if (!profile.desiredSalary?.trim()) return false;
    if (!profile.professionalSummary?.trim()) return false;
    if (profile.skills?.length < 3) return false;
    if (profile.workExperience?.length === 0 && profile.education?.length === 0) return false;
    
    return true;
};

const Jobs = () => {
    const { currentUser } = useAuth();
    const location = useLocation();
    const [allJobs, setAllJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [displayedJobs, setDisplayedJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [similarJobs, setSimilarJobs] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [userData, setUserData] = useState(null);
    const [showProfileAlert, setShowProfileAlert] = useState(false);
    const [profileAlertMessage, setProfileAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');
    
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    
    const [filters, setFilters] = useState({
        province: '',
        city: '',
        workType: '',
        dateFilter: '',
        experienceLevel: ''
    });
    
    useEffect(() => {
        loadJobs();
        if (currentUser) {
            loadUserData();
        }
    }, [currentUser]);
    
    useEffect(() => {
        applyFiltersAndSearch();
    }, [allJobs, filters, searchTerm]);
    
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, searchTerm, itemsPerPage]);

    // Handle shared job links from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const jobId = params.get('job');
        if (jobId && allJobs.length > 0) {
            const job = allJobs.find(j => j.id === jobId);
            if (job) {
                setSelectedJob(job);
                setShowModal(true);
                const similar = getSimilarJobs(allJobs, job, 5);
                setSimilarJobs(similar);
            }
        }
    }, [location.search, allJobs]);
    
    const loadJobs = async () => {
        setLoading(true);
        const jobs = await getAllJobs();
        setAllJobs(jobs);
        setFilteredJobs(jobs);
        setDisplayedJobs(jobs);
        setLoading(false);
    };

    const loadUserData = async () => {
        try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
                setUserData(userDocSnap.data());
            }
            
            const candidateDocRef = doc(db, 'candidates', currentUser.uid);
            const candidateDocSnap = await getDoc(candidateDocRef);
            if (candidateDocSnap.exists()) {
                setUserProfile(candidateDocSnap.data());
            } else {
                setUserProfile({});
            }
        } catch (error) {
            console.error("Error loading user data:", error);
        }
    };
    
    const handleApply = async (job) => {
        // 1. Check if user is logged in
        if (!currentUser) {
            setProfileAlertMessage('Please login to apply for jobs');
            setShowProfileAlert(true);
            setAlertType('login');
            setTimeout(() => setShowProfileAlert(false), 4000);
            return;
        }

        // 2. Check if profile exists
        if (!userProfile) {
            setProfileAlertMessage('Please complete your profile before applying for jobs');
            setShowProfileAlert(true);
            setAlertType('profile');
            setTimeout(() => setShowProfileAlert(false), 4000);
            return;
        }

        // 3. Check if profile is complete
        const completion = getCompletionPercentage(userProfile, userData);
        const isComplete = isProfileComplete(userProfile, userData);

        if (!isComplete || completion < 100) {
            const missingFields = getMissingFields(userProfile, userData);
            const missingList = missingFields.map(f => `• ${f}`).join('\n');
            setProfileAlertMessage(
                `Profile ${completion}% complete. Please complete the following fields:\n${missingList}`
            );
            setShowProfileAlert(true);
            setAlertType('profile');
            setTimeout(() => setShowProfileAlert(false), 5000);
            return;
        }

        // 4. Check job type: Internal vs External
        if (job.jobType === 'external') {
            // External job - show friendly disclaimer
            if (window.confirm(
                '📢 This job is from an external website.\n\n' +
                'We\'ll take you to the original job posting where you can apply.\n\n' +
                '✅ Quick tip: Always check the company details before applying!\n\n' +
                'Ready to check it out?'
            )) {
                window.open(job.applyLink, '_blank');
            }
            return; // IMPORTANT: Exit here so it doesn't continue to internal job logic
        }

        // 5. Internal job - apply directly
        const result = await trackApplication(currentUser.uid, job.id);
        if (result.success) {
            alert('✅ Your application has been sent successfully!');
        }
    };
    
    const applyFiltersAndSearch = () => {
        let result = [...allJobs];
        
        if (filters.province) {
            result = result.filter(job => job.province === filters.province);
        }
        
        if (filters.city) {
            result = result.filter(job => job.city === filters.city);
        }
        
        if (filters.workType) {
            result = result.filter(job => job.workType === filters.workType);
        }
        
        if (filters.experienceLevel) {
            result = result.filter(job => job.experienceLevel === filters.experienceLevel);
        }
        
        if (filters.dateFilter) {
            const today = new Date();
            const limitDate = new Date();
            
            switch(filters.dateFilter) {
                case 'week':
                    limitDate.setDate(today.getDate() + 7);
                    break;
                case 'month':
                    limitDate.setDate(today.getDate() + 30);
                    break;
                case 'three-months':
                    limitDate.setDate(today.getDate() + 90);
                    break;
                default:
                    break;
            }
            
            result = result.filter(job => {
                const closingDate = new Date(job.closingDate);
                return closingDate <= limitDate && closingDate >= today;
            });
        }
        
        if (searchTerm && searchTerm.trim() !== '') {
            result = searchJobs(result, searchTerm);
            setIsSearching(true);
        } else {
            setIsSearching(false);
        }
        
        // Sort: Sponsored first, then internal, then external
        result.sort((a, b) => {
            // Sponsored jobs first
            if (a.isSponsored && !b.isSponsored) return -1;
            if (!a.isSponsored && b.isSponsored) return 1;
            // Then internal jobs
            if (a.jobType === 'internal' && b.jobType !== 'internal') return -1;
            if (a.jobType !== 'internal' && b.jobType === 'internal') return 1;
            return 0;
        });
        
        setFilteredJobs(result);
        
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        setDisplayedJobs(result.slice(indexOfFirstItem, indexOfLastItem));
    };
    
    const handleSearch = (term) => {
        setSearchTerm(term);
    };
    
    const handleFilterChange = (newFilters) => {
        setFilters({ ...filters, ...newFilters });
    };
    
    const clearFilters = () => {
        setFilters({ province: '', city: '', workType: '', dateFilter: '', experienceLevel: '' });
        setSearchTerm('');
    };
    
    const handleViewDetails = (job) => {
        setSelectedJob(job);
        setShowModal(true);
        
        const similar = getSimilarJobs(allJobs, job, 5);
        setSimilarJobs(similar);
    };
    
    const closeModal = () => {
        setShowModal(false);
        setSelectedJob(null);
        setSimilarJobs([]);
    };
    
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        const indexOfLastItem = pageNumber * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        setDisplayedJobs(filteredJobs.slice(indexOfFirstItem, indexOfLastItem));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    
    const changeItemsPerPage = (value) => {
        setItemsPerPage(value);
        setCurrentPage(1);
        const indexOfLastItem = value;
        setDisplayedJobs(filteredJobs.slice(0, indexOfLastItem));
    };
    
    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading jobs...</span>
            </div>
        );
    }
    
    return (
        <div className="container mx-auto px-4 py-8">
            <PageTitle 
                title="Jobs" 
                description="Browse thousands of entry-level jobs, internships, and graduate programs in South Africa"
            />
            {/* Profile Alert */}
            {showProfileAlert && (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[9999] w-full max-w-md animate-slideDown">
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-lg shadow-xl">
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <i className="fas fa-exclamation-triangle text-yellow-500 text-xl"></i>
                            </div>
                            <div className="ml-3 flex-1">
                                <p className="text-sm text-yellow-700 whitespace-pre-line font-medium">
                                    {profileAlertMessage}
                                </p>
                                {alertType === 'login' && (
                                    <button 
                                        onClick={() => {
                                            setShowProfileAlert(false);
                                            document.getElementById('loginBtn')?.click();
                                        }}
                                        className="mt-2 text-sm text-gradpath-green font-semibold hover:underline inline-flex items-center gap-1"
                                    >
                                        Login Now <i className="fas fa-arrow-right ml-1"></i>
                                    </button>
                                )}
                                {alertType === 'profile' && (
                                    <button 
                                        onClick={() => window.location.href = '/profile'}
                                        className="mt-2 text-sm text-gradpath-green font-semibold hover:underline inline-flex items-center gap-1"
                                    >
                                        Complete Profile <i className="fas fa-arrow-right ml-1"></i>
                                    </button>
                                )}
                            </div>
                            <button 
                                onClick={() => setShowProfileAlert(false)}
                                className="ml-4 text-yellow-500 hover:text-yellow-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="text-center mb-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-2">Job Opportunities</h1>
                <p className="text-gray-600">Find your perfect role - from internships to junior positions</p>
            </div>

            <div className="max-w-2xl mx-auto mb-6">
                <JobSearchBar 
                    onSearch={handleSearch}
                    jobs={allJobs}
                    placeholder="Search by job title, skills, company..."
                />
                {isSearching && (
                    <p className="text-sm text-gray-500 mt-2 text-center">
                        Showing results for: <span className="font-medium text-gradpath-green">"{searchTerm}"</span>
                        {filteredJobs.length === 0 && (
                            <span className="block text-yellow-600 mt-1">
                                <i className="fas fa-info-circle mr-1"></i>
                                No exact matches found. Try similar keywords or check spelling.
                            </span>
                        )}
                    </p>
                )}
            </div>
            
            <div className="flex flex-col lg:flex-row gap-8">
                <div className="lg:w-80">
                    <JobFilters 
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={clearFilters}
                    />
                </div>
                
                <div className="flex-1">
                    {displayedJobs.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl">
                            <i className="fas fa-search text-5xl text-gray-300 mb-4"></i>
                            <p className="text-gray-500">No jobs found matching your criteria</p>
                            <button 
                                onClick={clearFilters}
                                className="mt-4 text-gradpath-green hover:underline"
                            >
                                Clear all filters and search
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {displayedJobs.map(job => (
                                    <JobCard 
                                        key={job.id}
                                        job={job}
                                        onViewDetails={handleViewDetails}
                                    />
                                ))}
                            </div>
                            
                            <Pagination
                                currentPage={currentPage}
                                totalPages={Math.ceil(filteredJobs.length / itemsPerPage)}
                                itemsPerPage={itemsPerPage}
                                totalItems={filteredJobs.length}
                                onPageChange={paginate}
                                onItemsPerPageChange={changeItemsPerPage}
                            />
                        </>
                    )}
                </div>
            </div>
            
            <JobDetailModal
                job={selectedJob}
                similarJobs={similarJobs}
                isOpen={showModal}
                onClose={closeModal}
                onApply={handleApply}
                onViewSimilar={handleViewDetails}
            />
        </div>
    );
};

export default Jobs;